import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus, Search, Users } from 'lucide-react';
import type { EmployeeListItem, Paginated } from '@workforce/shared';
import { api, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useDepartments, useReference } from '@/lib/queries';
import { country, countryName, employmentStatus, flag, workArrangement } from '@/lib/format';
import { t } from '@/lib/i18n';
import { Avatar, Badge, Button, EmptyState, ErrorBanner, Input, PageHeader, Pagination, Select, Spinner, statusTone } from '@/components/ui';

export function EmployeesPage() {
  const { user, can } = useAuth();
  const nav = useNavigate();
  const isAdmin = can('employees.write');
  const [q, setQ] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [status, setStatus] = useState('');
  const [arrangement, setArrangement] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const deps = useDepartments();
  const ref = useReference();

  const list = useQuery({
    queryKey: ['employees', { q, departmentId, countryCode, status, arrangement, includeInactive, page }],
    queryFn: () =>
      api.get<Paginated<EmployeeListItem>>(
        `/employees${qs({ q, departmentId, countryCode, status, workArrangement: arrangement, includeInactive: includeInactive || undefined, page, pageSize: 25 })}`,
      ),
    placeholderData: keepPreviousData,
  });

  const reset = (fn: () => void) => {
    fn();
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        title={user?.role === 'manager' ? t('My Team') : t('Employees')}
        subtitle={user?.role === 'manager' ? t('Employees who report to you') : t('Everyone in the company, across all countries')}
        actions={
          isAdmin && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => nav('/employees/new')}>
              {t('Add employee')}
            </Button>
          )
        }
      />

      <div className="card mb-4 grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
        <div className="relative lg:col-span-2">
          <Search className="pointer-events-none absolute start-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder={t('Search name, ID, e-mail, position, city…')} className="ps-9" value={q} onChange={(e) => reset(() => setQ(e.target.value))} />
        </div>
        <Select value={departmentId} onChange={(e) => reset(() => setDepartmentId(e.target.value))} aria-label={t('Department')}>
          <option value="">{t('All departments')}</option>
          {deps.data?.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
            </option>
          ))}
        </Select>
        <Select value={countryCode} onChange={(e) => reset(() => setCountryCode(e.target.value))} aria-label={t('Country')}>
          <option value="">{t('All countries')}</option>
          {ref.data?.countries.map((c) => (
            <option key={c.code} value={c.code}>
              {countryName(c.code, c.name)}
            </option>
          ))}
        </Select>
        <div className="flex gap-2 lg:col-span-2">
          <Select value={status} onChange={(e) => reset(() => setStatus(e.target.value))} aria-label={t('Status')} className="flex-1">
            <option value="">{t('Any status')}</option>
            {ref.data?.employmentStatuses.map((s) => (
              <option key={s.value} value={s.value}>
                {t(s.label)}
              </option>
            ))}
          </Select>
          <Select value={arrangement} onChange={(e) => reset(() => setArrangement(e.target.value))} aria-label={t('Work arrangement')} className="flex-1">
            <option value="">{t('Office & remote')}</option>
            {ref.data?.workArrangements.map((w) => (
              <option key={w.value} value={w.value}>
                {t(w.label)}
              </option>
            ))}
          </Select>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2 lg:col-span-6">
          <input type="checkbox" checked={includeInactive} onChange={(e) => reset(() => setIncludeInactive(e.target.checked))} className="rounded border-slate-300" />
          {t('Include deactivated employees')}
        </label>
      </div>

      <ErrorBanner error={list.error} />
      <div className="card overflow-hidden">
        {list.isLoading ? (
          <Spinner />
        ) : !list.data?.items.length ? (
          <EmptyState
            icon={<Users className="h-6 w-6" />}
            title={t('No employees found')}
            description={q || departmentId || countryCode || status || arrangement ? t('Try clearing the filters.') : isAdmin ? t('Add your first employee to get started.') : t('Nobody is assigned to you yet.')}
          />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="th">{t('Employee')}</th>
                    <th className="th">{t('Department / Position')}</th>
                    <th className="th">{t('Location & time zone')}</th>
                    <th className="th hidden 2xl:table-cell">{t('Manager')}</th>
                    <th className="th">{t('Status')}</th>
                    <th className="th hidden lg:table-cell">{t('Login')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {list.data.items.map((e) => (
                    <tr key={e.id} className="cursor-pointer hover:bg-slate-50" onClick={() => nav(`/employees/${e.id}`)}>
                      <td className="td">
                        <div className="flex items-center gap-3">
                          <Avatar name={e.fullName} size="sm" />
                          <div>
                            <Link to={`/employees/${e.id}`} className="font-medium text-slate-900 hover:text-brand-700" onClick={(ev) => ev.stopPropagation()}>
                              {e.fullName}
                            </Link>
                            <p className="text-xs text-slate-500">
                              {e.employeeCode} · {e.email}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="td">
                        <p>{e.departmentName ?? '—'}</p>
                        <p className="text-xs text-slate-500">{e.position ?? ''}</p>
                      </td>
                      <td className="td">
                        <p>
                          {flag(e.countryCode)} {e.city ? `${e.city}, ` : ''}
                          {country(e.countryCode)}
                        </p>
                        <p className="text-xs text-slate-500">
                          {e.timezone ?? '—'} · {e.workArrangement === 'office' && e.officeName ? e.officeName : t(workArrangement(e.workArrangement))}
                        </p>
                      </td>
                      <td className="td hidden 2xl:table-cell">{e.managerName ?? '—'}</td>
                      <td className="td">
                        <Badge tone={statusTone(e.employmentStatus)}>{t(employmentStatus(e.employmentStatus))}</Badge>
                      </td>
                      <td className="td hidden lg:table-cell">{e.hasAccount ? <Badge tone="brand">{e.role ? t(e.role) : ''}</Badge> : <span className="text-xs text-slate-400">{t('No login')}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <ul className="divide-y divide-slate-100 md:hidden">
              {list.data.items.map((e) => (
                <li key={e.id}>
                  <Link to={`/employees/${e.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                    <Avatar name={e.fullName} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-900">{e.fullName}</p>
                      <p className="truncate text-xs text-slate-500">
                        {e.position ?? e.employeeCode} · {flag(e.countryCode)} {e.city ?? country(e.countryCode)}
                      </p>
                    </div>
                    <Badge tone={statusTone(e.employmentStatus)}>{t(employmentStatus(e.employmentStatus))}</Badge>
                  </Link>
                </li>
              ))}
            </ul>
            <Pagination page={page} pageSize={25} total={list.data.total} onPage={setPage} />
          </>
        )}
      </div>
    </div>
  );
}
